import { useState, useEffect, memo } from "react";
import { v4 as uuidv4 } from "uuid";
// Custom components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
// Materials
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
// Helpers
import { formatNumberWithCommas } from "@/Common/helpers/Modifiers/formatters";
import { errorLogger } from "@/Common/helpers/logger";
// Types
import { IGetPayrollData, IGetPayrollSubmit, IGetPayrollDataRow } from "@/Washouts/types/PayrollClosing/queries";

const MIN_WIDTH_CELL: Record<string, string> = {
	"Company ID": "payroll-preview-table-cell-with-min-width-140",
	Amount: "payroll-preview-table-cell-with-min-width-80",
};

const MIN_WIDTH_HEADER: Record<string, string> = {
	"Company ID": "payroll-preview-table-cell-with-min-width-140",
	Amount: "payroll-preview-table-cell-with-min-width-80",
};

const COLUMNS_ALIAS: Record<string, string> = {
	"Display ID": "Company ID",
};

const buildData = (data: IGetPayrollDataRow[]) => {
	const values: PayrollDataValues[] = [];
	for (let i = 0; i < data.length; ++i) {
		values.push({ _id: uuidv4() });
		for (const entry of data[i].row) {
			if (Object.values(COLUMNS_ALIAS).includes(entry.label)) {
				continue;
			} else if (entry.label in COLUMNS_ALIAS) {
				values[i][COLUMNS_ALIAS[entry.label]] = entry.value;
			} else {
				values[i][entry.label] = entry.value;
			}
		}
	}
	return values;
};

const buildColumn = (data: IGetPayrollData | IGetPayrollSubmit): GridColDef[] => {
	const labels: GridColDef[] = [];
	let charCount = 0;

	data.columns.forEach((entry) => {
		if (entry.label in COLUMNS_ALIAS) {
			return;
		}
		charCount += entry.label.length;

		let cellClass = "table-small-cell " + (MIN_WIDTH_CELL[entry.label] ?? "");
		let headerClass = "table-small-header show-as-column " + (MIN_WIDTH_HEADER[entry.label] ?? "");
		switch (entry.columnType) {
			case "Amount":
				cellClass += " cell-with-dollar-symbol";
				headerClass += " fs-12";
				labels.push({
					field: entry.label,
					headerName: entry.label,
					align: "right",
					headerClassName: headerClass,
					cellClassName: cellClass,
					valueFormatter: (value) => {
						if (!value || value === "") {
							return "";
						}
						try {
							return formatNumberWithCommas(parseFloat(value).toFixed(2).toString());
						} catch (error) {
							return error;
						}
					},
				});
				break;
			case "Number":
				cellClass += " payroll-preview-table-cell-right";
				headerClass += " table-header-align-right fs-12";
				labels.push({
					field: entry.label,
					headerName: entry.label,
					align: "right",
					headerClassName: headerClass,
					cellClassName: cellClass,
				});
				break;
			case "String":
			default:
				cellClass += " payroll-preview-table-cell";
				headerClass += " fs-12";
				labels.push({
					field: entry.label,
					headerName: entry.label,
					headerClassName: headerClass,
					cellClassName: cellClass,
				});
		}
	});

	labels.forEach((label) => {
		label.flex = label.headerName ? label.headerName.length / charCount : 1;
	});

	return labels;
};

interface PayrollDataValues {
	_id: string;
	[key: string]: string;
}

type PayrollPreviewTableProps = {
	data?: IGetPayrollData | IGetPayrollSubmit | null;
};

export const PayrollPreviewTable = memo((props: Readonly<PayrollPreviewTableProps>) => {
	const apiRef = useGridApiRef();
	const [columns, setColumns] = useState<GridColDef[]>([]);
	const [data, setData] = useState<PayrollDataValues[]>([]);

	useEffect(() => {
		if (!props.data) {
			return () => {};
		}

		try {
			setColumns(buildColumn(props.data));
			setData(buildData(props.data.data));
		} catch (error) {
			errorLogger(error as Error);
		}

		return () => {
			setColumns([]);
			setData([]);
		};
	}, [props, apiRef]);

	return (
		<DataSmartTable
			apiRef={apiRef}
			columns={columns}
			rows={data}
			headerHeight={30}
			rowHeight={22}
			hideFooter
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			slots={{
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				noRowsOverlay: {
					title: "No commissions have been generated for this commission cycle.",
				},
			}}
			customProps={{
				tableContainerStyle: { height: "100%", width: "100%" },
				tableClassName: {
					"functional-table": true,
				},
				hide: false,
			}}
		/>
	);
});
